import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Power, Users } from "lucide-react";
import {
  listAutomationRuns,
  listEmailAutomations,
  previewAutomationReach,
  saveEmailAutomation,
  setEmailAutomationActive,
  getAnchorFallbackSetting,
  setAnchorFallbackSetting,
  type AutomationListItem,
} from "@/lib/email-automations.functions";
import { BEGLEITMAIL_TEMPLATES } from "@/lib/email-templates/catalog";
import { LOOKBACK_DAYS, MAX_MAILS_PER_RUN } from "@/lib/email-automations/schedule";
import { useAdminSession } from "@/hooks/use-admin-session";

export const Route = createFileRoute("/_admin/admin/emails/automationen")({
  component: AutomationsPage,
});

const TRIGGER_LABELS: Record<string, string> = {
  module_assigned: "Modul wurde zugewiesen",
  client_date: "Datum am Kunden",
  election_outcome_set: "Wahlausgang hinterlegt",
};

const DATE_FIELD_LABELS: Record<string, string> = {
  election_day: "Wahltag",
  term_start: "Amtsbeginn",
};

const MANDATE_TYPES = [
  "buergermeister",
  "oberbuergermeister",
  "landrat",
  "direktkandidat",
  "partei_gliederung",
  "verband",
  "kommune",
  "personalratsliste",
  "sonstiges",
] as const;

const STATUS_LABELS: Record<string, string> = {
  sent: "versendet",
  skipped: "übersprungen",
  unsubscribed: "abbestellt",
  failed: "fehlgeschlagen",
};

type FormState = {
  id: string | null;
  name: string;
  trigger: "module_assigned" | "client_date" | "election_outcome_set";
  triggerDateField: "election_day" | "term_start" | null;
  offsetDays: number;
  templateKey: string;
  conditionMandateTypes: string[];
  conditionCategories: string[];
  conditionModuleKey: string | null;
  fallbackElectionDay: boolean;
  active: boolean;
};

const emptyForm = (): FormState => ({
  id: null,
  name: "",
  trigger: "module_assigned",
  triggerDateField: null,
  offsetDays: 0,
  templateKey: BEGLEITMAIL_TEMPLATES[0]?.key ?? "",
  conditionMandateTypes: [],
  conditionCategories: [],
  conditionModuleKey: null,
  fallbackElectionDay: false,
  active: false,
});

function toForm(item: AutomationListItem): FormState {
  return {
    id: item.id,
    name: item.name,
    trigger: item.trigger,
    triggerDateField: item.triggerDateField,
    offsetDays: item.offsetDays,
    templateKey: item.templateKey,
    conditionMandateTypes: item.conditionMandateTypes,
    conditionCategories: item.conditionCategories,
    conditionModuleKey: item.conditionModuleKey,
    fallbackElectionDay: item.fallbackElectionDay,
    active: item.active,
  };
}

function describeRule(item: AutomationListItem): string {
  const base =
    item.trigger === "client_date"
      ? DATE_FIELD_LABELS[item.triggerDateField ?? "election_day"]
      : TRIGGER_LABELS[item.trigger];
  const offset =
    item.offsetDays === 0
      ? "am selben Tag"
      : item.offsetDays > 0
        ? `${item.offsetDays} Tage danach`
        : `${Math.abs(item.offsetDays)} Tage davor`;
  return `${base} · ${offset}`;
}

function AutomationsPage() {
  const session = useAdminSession();
  const queryClient = useQueryClient();
  const fetchList = useServerFn(listEmailAutomations);
  const fetchRuns = useServerFn(listAutomationRuns);
  const save = useServerFn(saveEmailAutomation);
  const toggle = useServerFn(setEmailAutomationActive);
  const preview = useServerFn(previewAutomationReach);

  const [form, setForm] = useState<FormState | null>(null);
  const [reach, setReach] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "email-automations"],
    queryFn: () => fetchList(),
  });
  const runs = useQuery({
    queryKey: ["admin", "email-automation-runs"],
    queryFn: () => fetchRuns({ data: { automationId: null, clientId: null } }),
  });

  const templateOptions = useMemo(() => BEGLEITMAIL_TEMPLATES, []);

  const saveMutation = useMutation({
    mutationFn: (state: FormState) =>
      save({
        data: {
          id: state.id,
          values: {
            name: state.name,
            trigger: state.trigger,
            triggerDateField: state.trigger === "client_date" ? state.triggerDateField : null,
            offsetDays: state.offsetDays,
            templateKey: state.templateKey,
            conditionMandateTypes: state.conditionMandateTypes as never,
            conditionCategories: state.conditionCategories as never,
            conditionModuleKey: state.conditionModuleKey as never,
            fallbackElectionDay: state.fallbackElectionDay,
            active: state.active,
          },
        },
      }),
    onSuccess: () => {
      toast.success("Automation gespeichert.");
      setForm(null);
      setReach(null);
      void queryClient.invalidateQueries({ queryKey: ["admin", "email-automations"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen."),
  });

  const toggleMutation = useMutation({
    mutationFn: (vars: { id: string; active: boolean }) => toggle({ data: vars }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "email-automations"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Änderung fehlgeschlagen."),
  });

  const previewMutation = useMutation({
    mutationFn: (state: FormState) =>
      preview({
        data: {
          trigger: state.trigger,
          triggerDateField: state.trigger === "client_date" ? state.triggerDateField : null,
          offsetDays: state.offsetDays,
          conditionMandateTypes: state.conditionMandateTypes,
          conditionCategories: state.conditionCategories,
          conditionModuleKey: state.conditionModuleKey,
          fallbackElectionDay: state.fallbackElectionDay,
        },
      }),
    onSuccess: (res: any) => {
      setReach(
        `Trifft aktuell ${res.matching} Kunden · ${res.due} heute fällig · ${res.upcoming} künftig fällig · ${res.withoutRecipient} ohne Cockpit-Zugang · ${res.withoutTermStart} ohne gepflegten Amtsantritt`,
      );
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Vorschau fehlgeschlagen."),
  });

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-8">
      <Link
        to="/admin/emails"
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zurück zu den E-Mails
      </Link>

      <h1 className="text-xl font-semibold text-foreground">Automationen für Begleitmails</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Begleitmails gehen einmal je Kundin oder Kunde und Automation heraus. Der Versand läuft
        einmal täglich; Fälligkeiten, die mehr als {LOOKBACK_DAYS} Tage zurückliegen, werden nicht
        nachgeholt. Pro Lauf werden höchstens {MAX_MAILS_PER_RUN} Begleitmails verschickt.
        Kundenkonten können Begleitmails im Cockpit abbestellen; Zugangs- und Passwortmails bleiben
        davon unberührt.
      </p>

      {/* Ticket AD3: Automationen führen dürfen auch Editorinnen und Editoren;
          die globale Ankerregel bleibt bei den Administratoren. */}
      <AnchorFallbackCard canEdit={session.isAdmin} />
      {!session.isAdmin && (
        <p className="mt-3 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Als Editorin oder Editor können Sie einzelne Automationen anlegen und führen. Die
          Grundeinstellung des Versandverhaltens ist Administratoren vorbehalten.
        </p>
      )}



      {isLoading && (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Automationen werden
          geladen …
        </div>
      )}
      {error && (
        <p className="mt-6 text-sm text-destructive">
          Automationen konnten nicht geladen werden: {(error as Error).message}
        </p>
      )}

      {(session.isAdmin || session.isEditor) && !form && (
        <button
          type="button"
          onClick={() => {
            setForm(emptyForm());
            setReach(null);
          }}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> Neue Automation
        </button>
      )}

      {form && (
        <section className="mt-6 rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {form.id ? "Automation bearbeiten" : "Neue Automation"}
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm">
              <span className="text-muted-foreground">Bezeichnung</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Vorlage</span>
              <select
                value={form.templateKey}
                onChange={(e) => setForm({ ...form, templateKey: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              >
                {templateOptions.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Auslöser</span>
              <select
                value={form.trigger}
                onChange={(e) =>
                  setForm({
                    ...form,
                    trigger: e.target.value as FormState["trigger"],
                    triggerDateField:
                      e.target.value === "client_date" ? (form.triggerDateField ?? "term_start") : null,
                  })
                }
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              >
                {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {form.trigger === "client_date" && (
              <label className="text-sm">
                <span className="text-muted-foreground">Datumsfeld</span>
                <select
                  value={form.triggerDateField ?? "term_start"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      triggerDateField: e.target.value as FormState["triggerDateField"],
                    })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
                >
                  {Object.entries(DATE_FIELD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="text-muted-foreground">Zeitversatz in Tagen (negativ = davor)</span>
              <input
                type="number"
                value={form.offsetDays}
                onChange={(e) => setForm({ ...form, offsetDays: Number(e.target.value) })}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="text-muted-foreground">Nur bei Modul</span>
              <select
                value={form.conditionModuleKey ?? ""}
                onChange={(e) =>
                  setForm({ ...form, conditionModuleKey: e.target.value || null })
                }
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              >
                <option value="">ohne Einschränkung</option>
                <option value="hundert_tage">100 Tage im Amt</option>
              </select>
            </label>
            {form.trigger === "client_date" && form.triggerDateField === "term_start" && (
              <label className="inline-flex items-start gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.fallbackElectionDay}
                  onChange={(e) => setForm({ ...form, fallbackElectionDay: e.target.checked })}
                  className="mt-1"
                />
                <span className="text-muted-foreground">
                  Fehlt der Amtsantritt, den Wahltag zuzüglich Standardversatz verwenden. Ein
                  gepflegter Amtsantritt hat immer Vorrang. Fehlen beide Daten, entfällt der
                  Versand.
                </span>
              </label>
            )}
          </div>

          <fieldset className="mt-4">
            <legend className="text-sm text-muted-foreground">Kundenkategorie</legend>
            <div className="mt-2 flex flex-wrap gap-3 text-sm">
              {["agenturkunde", "kurskunde", "interessent"].map((cat) => (
                <label key={cat} className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.conditionCategories.includes(cat)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        conditionCategories: e.target.checked
                          ? [...form.conditionCategories, cat]
                          : form.conditionCategories.filter((c) => c !== cat),
                      })
                    }
                  />
                  {cat === "agenturkunde"
                    ? "Agenturkunde"
                    : cat === "kurskunde"
                      ? "Kurskunde"
                      : "Interessent"}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Ohne Auswahl gilt die Automation für Agentur- und Kurskunden. Interessenten werden
              nur erfasst, wenn die Kategorie hier ausdrücklich gewählt ist.
            </p>
          </fieldset>


          <fieldset className="mt-4">
            <legend className="text-sm text-muted-foreground">Mandatstyp</legend>
            <div className="mt-2 flex flex-wrap gap-3 text-sm">
              {MANDATE_TYPES.map((mt) => (
                <label key={mt} className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.conditionMandateTypes.includes(mt)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        conditionMandateTypes: e.target.checked
                          ? [...form.conditionMandateTypes, mt]
                          : form.conditionMandateTypes.filter((c) => c !== mt),
                      })
                    }
                  />
                  {mt}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="mt-4 inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Automation ist aktiv
          </label>

          {reach && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-foreground">
              <Users className="h-4 w-4" aria-hidden="true" /> {reach}
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={previewMutation.isPending}
              onClick={() => previewMutation.mutate(form)}
              className="rounded-md border border-border px-3 py-2 text-sm font-semibold"
            >
              Reichweite prüfen
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending || form.name.trim().length < 3}
              onClick={() => saveMutation.mutate(form)}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
            >
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Speichern
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(null);
                setReach(null);
              }}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground"
            >
              Abbrechen
            </button>
          </div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="text-sm font-semibold text-foreground">Alle Automationen</h2>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          {(data ?? []).map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {item.name}{" "}
                  <span className={item.active ? "text-brand-wahlen" : "text-muted-foreground"}>
                    · {item.active ? "aktiv" : "inaktiv"}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {item.templateName} · {describeRule(item)} · {item.sentCount} versendet
                </p>
              </div>
              {(session.isAdmin || session.isEditor) && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setForm(toForm(item));
                      setReach(null);
                    }}
                    className="rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      toggleMutation.mutate({ id: item.id, active: !item.active })
                    }
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    <Power className="h-4 w-4" aria-hidden="true" />
                    {item.active ? "Stilllegen" : "Aktivieren"}
                  </button>
                </>
              )}
            </li>
          ))}
          {(data ?? []).length === 0 && !isLoading && (
            <li className="p-4 text-sm text-muted-foreground">
              Noch keine Automation angelegt.
            </li>
          )}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold text-foreground">Versandhistorie</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Die letzten Einträge je Kunde und Automation. Automationen werden nie gelöscht, damit
          dieser Bezug erhalten bleibt.
        </p>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          {(runs.data ?? []).map((r) => (
            <li key={r.id} className="p-4 text-sm">
              <p className="font-semibold text-foreground">
                {r.organizationName} · {r.automationName}
              </p>
              <p className="text-muted-foreground">
                Fällig am {r.dueOn} · {STATUS_LABELS[r.status] ?? r.status}
                {r.skipReason ? ` · ${r.skipReason}` : ""}
              </p>
            </li>
          ))}
          {(runs.data ?? []).length === 0 && (
            <li className="p-4 text-sm text-muted-foreground">Noch keine Einträge.</li>
          )}
        </ul>
      </section>
    </div>
  );
}

/**
 * Ticket Z16: Standardversatz Wahltag bis Amtsantritt.
 *
 * Greift nur bei Automationen mit Rückfall auf den Wahltag und nur, wenn beim
 * Kunden kein Amtsantritt gepflegt ist.
 */
function AnchorFallbackCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const load = useServerFn(getAnchorFallbackSetting);
  const store = useServerFn(setAnchorFallbackSetting);
  const [draft, setDraft] = useState<string | null>(null);

  const setting = useQuery({
    queryKey: ["admin", "email-anchor-fallback"],
    queryFn: () => load(),
  });

  const mutation = useMutation({
    mutationFn: (days: number) => store({ data: { days } }),
    onSuccess: () => {
      toast.success("Standardversatz gespeichert.");
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ["admin", "email-anchor-fallback"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen."),
  });

  const current = setting.data?.days ?? 60;
  const value = draft ?? String(current);

  return (
    <section className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
      <h2 className="text-sm font-semibold text-foreground">
        Standardversatz Wahltag bis Amtsantritt
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Ist bei einer Kundin oder einem Kunden kein Amtsantritt gepflegt, rechnen Automationen mit
        Rückfall auf den Wahltag mit diesem Versatz. Ein gepflegtes Amtsantrittsdatum ist immer
        genauer und hat Vorrang.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          type="number"
          min={0}
          max={365}
          value={value}
          disabled={!canEdit || setting.isLoading}
          onChange={(e) => setDraft(e.target.value)}
          className="w-24 rounded-md border border-border bg-background px-3 py-2 text-sm"
          aria-label="Standardversatz in Tagen"
        />
        <span className="text-sm text-muted-foreground">Tage</span>
        {canEdit && (
          <button
            type="button"
            disabled={draft === null || mutation.isPending}
            onClick={() => mutation.mutate(Number.parseInt(value, 10))}
            className="rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-50"
          >
            Speichern
          </button>
        )}
      </div>
    </section>
  );
}
